import * as React from 'react';
import styled from 'styled-components';
import AppState from '@webapp/store/state';
import { connect } from 'react-redux';
import { getMainDomain } from '@webapp/utils/get-artist-site-url';
import { TracPage } from '@services/tracpage/types/trac-page';
import { TracPageType } from '@services/tracpage/types/trac-page-type';
import TracLink from '@services/tracpage/types/trac-link';
import { Translate } from 'react-localize-redux';
import { Typography } from '@components/basics/typography';
import { artistSiteUrl } from '@webapp/store/artist/selectors';
import ErrorBoundary from '@components/error-boundary';
import { Dispatch } from 'redux';
import { GetSubdomain } from '@webapp/store/subdomains/action-creators';

const getSiteState = (siteData: TracPage | TracLink): string => {
  return btoa(unescape(encodeURIComponent(JSON.stringify(siteData))));
};

const mapStateToProps = (state: AppState, props: { pageType?: string }) => ({
  isMobile: state.ui.mobile,
  siteUrl: artistSiteUrl(state, true),
  subdomainUrl: state.subdomain.currentSubdomain?.subdomain,
  state: getSiteState(
    props.pageType === 'tracpage'
      ? state.tracpage.artistPage
      : state.tracpage.trackLinks.editor
  ),
  artistId: state.artist.active?.id,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getSubdomainByOwnerId: (artistShortId: string) =>
    dispatch(new GetSubdomain(artistShortId)),
});

const postToRef = (
  contentWindow: Window,
  state: string,
  destination: string
) => {
  if (!contentWindow) {
    return;
  }

  contentWindow.postMessage(
    {
      origin: 'trac-editor',
      payload: state,
    },
    destination
  );
};

interface SitePreviewCtrlProps {
  className?: string;
  subdomainUrl?: string;
  section?: string;
  state: string;
  pageType?: TracPageType;
  isMobile: boolean;
  siteUrl: string;
  artistId?: string;
  getSubdomainByOwnerId: (ownerId: string) => void;
}

const SitePreviewCtrl = ({
  state,
  subdomainUrl,
  section,
  className,
  isMobile,
  siteUrl,
  artistId,
  getSubdomainByOwnerId,
}: SitePreviewCtrlProps) => {
  const [isSubdomainRequested, setIsSubdomainRequested] = React.useState(false);
  const [ready, setReady] = React.useState<number>();
  const [ref, setRef] = React.useState<HTMLIFrameElement | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState(
    subdomainUrl &&
      `https://${subdomainUrl || '_local_preview'}.${getMainDomain()}`
  );

  React.useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      if (!(event.data?.origin === 'trac-artist-site' && event.data?.ready)) {
        return;
      }

      setReady(Number(new Date()));
    };

    window.addEventListener('message', receiveMessage, false);

    return () => {
      window.removeEventListener('message', receiveMessage);
    };
  }, [state]);

  React.useEffect(() => {
    if (subdomainUrl) {
      setPreviewUrl(
        `https://${subdomainUrl || '_local_preview'}.${getMainDomain()}`
      );
    }
  }, [subdomainUrl, isSubdomainRequested, setIsSubdomainRequested]);

  React.useEffect(() => {
    if (!subdomainUrl && !isSubdomainRequested && artistId) {
      getSubdomainByOwnerId(artistId);
      setIsSubdomainRequested(true);
    }
  }, [
    artistId,
    subdomainUrl,
    isSubdomainRequested,
    setIsSubdomainRequested,
    getSubdomainByOwnerId,
  ]);

  React.useEffect(() => {
    if (ref?.contentWindow && ready) {
      postToRef(
        ref?.contentWindow,
        state,
        `${previewUrl}${section ? `/${section}` : ''}?forceFresh=true`
      );
    }
  }, [ready, state, ref, previewUrl, section]);

  return (
    <PreviewWrapper className={className}>
      {!isMobile ? (
        <StyledPreviewLink>
          <a href={siteUrl} target="_blank" rel="noopener noreferrer">
            <StyledLink type="link">
              <Translate id="ARTIST_SITE.SIDEBAR.LINK" />
            </StyledLink>
          </a>
        </StyledPreviewLink>
      ) : null}
      {state && previewUrl ? (
        <ErrorBoundary>
          <iframe
            ref={setRef}
            id="artistSitePreview"
            className="iframe-preview"
            title="Artist Site Preview"
            src={`${previewUrl}${section ? `/${section}` : ''}?forceFresh=true`}
          />
        </ErrorBoundary>
      ) : null}
    </PreviewWrapper>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SitePreviewCtrl);

const StyledPreviewLink = styled.div`
  text-align: center;
  pointer-events: auto !important;
`;
const StyledLink = styled(Typography)`
  color: ${({ theme }) => theme.colors.accent};
`;
const PreviewWrapper = styled.div`
  width: 100%;
  height: 640px;
  position: relative;
  margin-bottom: 40px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.header};
  overflow: hidden;
  box-shadow: ${({ theme }) =>
    `${theme.general.boxShadow} ${theme.colors.shadow}`};

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    height: 240px;
  }

  .iframe-preview {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    border-radius: 4px;
    overflow: hidden;

    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      margin-top: 10rem;
      zoom: 0.25;
      transform: scale(0.25);
      transform-origin: 0 0;
      width: calc(100% * 4);
      height: calc(100% * 4);
    }
  }
`;
